// Inspecciones (F3a, M41): tipos y etiquetas comunes del motor. Tablas insp_* (el módulo anterior, InspectionsPage.tsx,
// usa inspections e inspection_responses y alimenta el GT-14; no se toca).
//  - Plantilla → inspección (con la FOTO de la plantilla) → respuestas por ítem → hallazgos → seguimiento con evidencia.
//  - Punto de extensión para Mantenimiento: asset_id (inspección y programación) y asset_class (plantilla), null en F3a.

export type TipoRespuesta = 'cumple' | 'si_no' | 'numero' | 'texto' | 'foto';
export type Frecuencia = 'unica' | 'semanal' | 'mensual' | 'trimestral' | 'semestral' | 'anual';
export type Riesgo = 'bajo' | 'medio' | 'alto';
export type ReglaFoto = 'opcional' | 'obligatoria_no_cumple' | 'obligatoria';
export type EstadoInspeccion = 'programada' | 'en_curso' | 'completada' | 'cancelada';
export type EstadoHallazgoGuardado = 'abierto' | 'en_proceso' | 'cerrado';
// "vencido" no se guarda: se calcula al vuelo (fecha límite pasada y sin cerrar).
export type EstadoHallazgo = EstadoHallazgoGuardado | 'vencido';
export type CategoriaPlantilla = 'actos_inseguros' | 'condiciones_inseguras' | 'incendio' | 'epp' | 'quimicos' | 'anexo1' | 'propia';
export type ValorRespuesta = 'cumple' | 'no_cumple' | 'no_aplica' | 'si' | 'no';
export type MetodoFirma = 'pantalla' | 'papel' | 'firmaec';
export type RolFirma = 'inspector' | 'responsable';
export type TipoArchivo = 'foto_item' | 'foto_hallazgo' | 'evidencia_cierre';

export const ETIQUETA_TIPO_RESPUESTA: Record<TipoRespuesta, string> = {
  cumple: 'Cumple / No cumple / No aplica', si_no: 'Sí / No', numero: 'Número con unidad', texto: 'Texto', foto: 'Foto',
};
export const ETIQUETA_FRECUENCIA: Record<Frecuencia, string> = {
  unica: 'Única', semanal: 'Semanal', mensual: 'Mensual', trimestral: 'Trimestral', semestral: 'Semestral', anual: 'Anual',
};
export const FRECUENCIAS: Frecuencia[] = ['unica', 'semanal', 'mensual', 'trimestral', 'semestral', 'anual'];
export const ETIQUETA_RIESGO: Record<Riesgo, string> = { bajo: 'Bajo', medio: 'Medio', alto: 'Alto' };
export const RIESGOS: Riesgo[] = ['bajo', 'medio', 'alto'];
export const ETIQUETA_REGLA_FOTO: Record<ReglaFoto, string> = {
  opcional: 'Opcional', obligatoria_no_cumple: 'Obligatoria si no cumple', obligatoria: 'Siempre obligatoria',
};
export const ETIQUETA_ESTADO_INSPECCION: Record<EstadoInspeccion | 'atrasada', string> = {
  programada: 'Programada', atrasada: 'Atrasada', en_curso: 'En curso', completada: 'Completada', cancelada: 'Cancelada',
};
export const ETIQUETA_ESTADO_HALLAZGO: Record<EstadoHallazgo, string> = {
  abierto: 'Abierto', en_proceso: 'En proceso', cerrado: 'Cerrado', vencido: 'Vencido',
};
export const ETIQUETA_CATEGORIA: Record<CategoriaPlantilla, string> = {
  actos_inseguros: 'Actos inseguros', condiciones_inseguras: 'Condiciones inseguras', incendio: 'Recursos contra incendio', epp: 'EPP',
  quimicos: 'Productos químicos', anexo1: 'Anexo 1', propia: 'Propia',
};
export const ETIQUETA_VALOR: Record<ValorRespuesta, string> = {
  cumple: 'Cumple', no_cumple: 'No cumple', no_aplica: 'No aplica', si: 'Sí', no: 'No',
};
export const ETIQUETA_METODO_FIRMA: Record<MetodoFirma, string> = {
  pantalla: 'Firma en pantalla', papel: 'Papel firmado (escaneado)', firmaec: 'PDF firmado con FirmaEC',
};
export const ETIQUETA_ROL_FIRMA: Record<RolFirma, string> = { inspector: 'Inspector', responsable: 'Responsable del área' };

// ── Foto de la plantilla (insp_foto_plantilla, M41) ──
export interface ItemFoto {
  id: string;
  text: string;
  help: string | null;
  response_type: TipoRespuesta;
  unit: string | null;
  num_min: number | null;
  num_max: number | null;
  required: boolean;
  creates_finding: boolean;
  fail_value: 'si' | 'no' | null;
  default_risk: Riesgo;
  photo_rule: ReglaFoto;
  anexo_item: string | null;
  reference: string | null;
  rule_key: string | null;
  position: number;
}
export interface SeccionFoto { id: string; title: string; position: number; items: ItemFoto[] }
export interface FotoPlantilla {
  template_id: string;
  code: string | null;
  name: string;
  category: CategoriaPlantilla;
  version: number;
  anexo_items: string[];
  sistema: boolean;
  sections: SeccionFoto[];
}

// ── Filas ──
export interface FilaPlantilla {
  id: string;
  company_id: string | null;
  code: string | null;
  name: string;
  description: string | null;
  category: CategoriaPlantilla;
  is_library: boolean;
  anexo_items: string[];
  default_frequency: Frecuencia | null;
  frequency_source: string | null;
  version: number;
  active: boolean;
  source_template_id: string | null;
  updated_at: string;
}

export interface FirmaInspeccion {
  rol: RolFirma;
  nombre: string;
  cedula: string;
  firmado_en: string;
  hash: string;
  metodo: MetodoFirma;
  firma_path?: string;
  archivo_path?: string;
}

export interface FilaInspeccion {
  id: string;
  company_id: string;
  site_id: string;
  area: string | null;
  template_id: string | null;
  template_name: string;
  template_version: number;
  template_snapshot: FotoPlantilla;
  schedule_id: string | null;
  asset_id: string | null;
  status: EstadoInspeccion;
  scheduled_date: string | null;
  performed_date: string | null;
  responsible_worker_id: string | null;
  responsible_role: string | null;
  observations: string | null;
  calendar_event_id: string | null;
  signed_at: string | null;
  sign_method: MetodoFirma | null;
  signatures: FirmaInspeccion[];
  pdf_path: string | null;
  sign_hash: string | null;
  reopen_history: { reabierto_en: string; reabierto_por: string | null; motivo: string }[];
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface FilaRespuesta {
  id?: string;
  inspection_id: string;
  item_key: string;
  value: ValorRespuesta | null;
  number_value: number | null;
  text_value: string | null;
  note: string | null;
}

export interface FilaHallazgo {
  id: string;
  company_id: string;
  site_id: string;
  inspection_id: string | null;
  item_key: string | null;
  origin: 'automatico' | 'manual';
  description: string;
  risk_level: Riesgo;
  corrective_action: string | null;
  responsible_worker_id: string | null;
  responsible_role: string | null;
  due_date: string | null;
  status: EstadoHallazgoGuardado;
  closed_at: string | null;
  closed_by: string | null;
  closure_note: string | null;
  created_at: string;
}

export interface FilaArchivo {
  id: string;
  company_id: string;
  inspection_id: string | null;
  item_key: string | null;
  finding_id: string | null;
  kind: TipoArchivo;
  storage_path: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
}

export interface FilaProgramacion {
  id: string;
  company_id: string;
  template_id: string;
  site_id: string;
  area: string | null;
  frequency: Frecuencia;
  start_date: string;
  end_date: string | null;
  responsible_worker_id: string | null;
  responsible_role: string | null;
  notes: string | null;
  active: boolean;
}

export interface SedeMin { id: string; name: string; active: boolean; is_main: boolean; canton_code?: string | null }
export interface TrabajadorMin { id: string; full_name: string }
/** Activo del inventario (M50) para la columna "Activo" de la tabla. */
export interface ActivoMin { id: string; code: string; name: string }

export const BUCKET_INSPECCIONES = 'inspection-files';
